import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, ChevronRight } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import ContactForm from "@/components/landing/ContactForm";

const CANONICAL = "https://sapeur-360.fr/contact";
const TITLE = "Contact — Sapeur-360 (Safetyrok SAS, Limoges)";
const DESC = "Contactez Sapeur-360 : demandez une démonstration de la plateforme robotisée, par téléphone au +33 (0)7 80 17 94 67 ou par email. Safetyrok SAS, 38 rue François Chénieux, 87000 Limoges.";

export default function ContactPage() {
  useEffect(() => {
    const updates = [
      ["__title__", null, TITLE],
      ['meta[name="description"]', "content", DESC],
      ['link[rel="canonical"]', "href", CANONICAL],
      ['meta[property="og:title"]', "content", TITLE],
      ['meta[property="og:description"]', "content", DESC],
      ['meta[property="og:url"]', "content", CANONICAL],
    ];
    const originals = updates.map(([sel, attr, val]) => {
      if (sel === "__title__") { const prev = document.title; document.title = val; return { titleMode: true, prev }; }
      const el = document.head.querySelector(sel);
      if (!el) return null;
      const prev = el.getAttribute(attr); el.setAttribute(attr, val); return { el, attr, prev };
    });
    return () => {
      originals.forEach((o) => {
        if (!o) return;
        if (o.titleMode) { document.title = o.prev; return; }
        if (o.prev === null) o.el.removeAttribute(o.attr); else o.el.setAttribute(o.attr, o.prev);
      });
    };
  }, []);

  // LocalBusiness structured data (local SEO)
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-localbusiness", "1");
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Sapeur-360 — Safetyrok SAS",
      image: "https://sapeur-360.fr/images/logo.jpg",
      url: CANONICAL,
      email: "contact@sapeur-360.fr",
      telephone: "+33780179467",
      address: {
        "@type": "PostalAddress",
        streetAddress: "38 rue François Chénieux",
        postalCode: "87000",
        addressLocality: "Limoges",
        addressCountry: "FR",
      },
      openingHours: "Mo-Fr 09:00-18:00",
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, []);

  const infos = [
    { icon: Phone, label: "Téléphone", value: "+33 (0)7 80 17 94 67", href: "tel:+33780179467" },
    { icon: Mail, label: "Email", value: "contact@sapeur-360.fr", href: "mailto:contact@sapeur-360.fr" },
    { icon: MapPin, label: "Adresse", value: "Safetyrok SAS — 38 rue François Chénieux, 87000 Limoges" },
    { icon: Clock, label: "Horaires", value: "Du lundi au vendredi, 9h – 18h" },
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A] antialiased">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-28 pb-16 lg:pt-36 lg:pb-20">
          <div className="absolute inset-0 blueprint-grid blueprint-grid-fade pointer-events-none" />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <nav className="flex items-center gap-2 text-sm text-[#52525B] mb-8" aria-label="Fil d'Ariane">
              <Link to="/" className="hover:text-[#0A0A0A] transition-colors">Accueil</Link>
              <ChevronRight className="h-4 w-4 text-[#A1A1AA]" />
              <span className="text-[#0A0A0A] font-semibold">Contact</span>
            </nav>
            <div className="max-w-2xl">
              <p className="font-mono-tech text-sm uppercase tracking-[0.2em] text-[#E63946] font-semibold">Contact</p>
              <h1 className="font-display mt-4 text-4xl md:text-5xl font-black tracking-tighter leading-[1.02]">
                Parlons de votre besoin.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-[#52525B]">
                Une question sur Sapeur-360 ? Envie d'une démonstration sur votre site ? Notre équipe
                vous répond rapidement. Contactez Safetyrok SAS à Limoges.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
              {infos.map((it) => (
                <div key={it.label} data-testid={`contact-info-${it.label}`}
                  className="flex items-start gap-4 bg-white border border-zinc-200 rounded-sm p-5">
                  <span className="grid h-11 w-11 place-items-center bg-[#0A0A0A] text-white rounded-sm shrink-0">
                    <it.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="font-mono-tech text-xs uppercase tracking-wider text-[#A1A1AA]">{it.label}</div>
                    {it.href ? (
                      <a href={it.href} className="font-bold hover:text-[#E63946] transition-colors">{it.value}</a>
                    ) : (
                      <div className="font-bold">{it.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="pb-4">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="rounded-sm overflow-hidden border border-zinc-200 h-[360px]">
              <iframe
                title="Localisation Safetyrok SAS — Limoges"
                src="https://www.google.com/maps?q=38+rue+Fran%C3%A7ois+Ch%C3%A9nieux+87000+Limoges&output=embed"
                width="100%" height="100%" style={{ border: 0 }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>

        <ContactForm />
      </main>
      <Footer />
    </div>
  );
}
